import { useMemo, useState } from 'react'
import { useAssignGateway, useAssignSlice, useBillers, useGateways, useRevokeSlice, useSetGatewayLabel } from '../../api/queries'
import type { BeneficiaryLine, GatewayEntry } from '../../api/types'
import { BffError } from '../../bff/errors'
import { useT, type Translate } from '../../i18n'
import { Button, DataTable, EmptyState, SelectField, Skeleton, StatusBadge, TextField, useToast, type Column } from '../../ui'
import screen from '../screen.module.css'
import { BeneficiariesPanel } from './BeneficiariesPanel'
import { GatewayNote } from './GatewayNote'
import styles from './gateways.module.css'

/**
 * The gateway mapping screen.
 *
 * Two things happen here and they are deliberately on one row rather than
 * behind an edit dialog: naming a gateway, and deciding whose payments it
 * carries. An internal user working through a list of unmapped gateways does
 * both for each one in turn, and a modal per gateway would triple the clicks
 * for the only workflow this screen has.
 *
 * The gateway list is derived from the warehouse — `SELECT DISTINCT
 * gateway_id` — so it is exactly the set Smobilpay has actually settled
 * through. There is no registry to fall out of sync with.
 */
function buildColumns(
  t: Translate,
  providers: ReadonlyArray<{ readonly biller_id: string; readonly name: string }>,
  actions: {
    readonly draftLabel: (g: GatewayEntry) => string
    readonly onLabelChange: (id: string, value: string) => void
    readonly onLabelSave: (g: GatewayEntry) => void
    readonly onAssign: (g: GatewayEntry, billerId: string) => void
    readonly onOpenBeneficiaries: (g: GatewayEntry) => void
    readonly busyId: string | null
  },
): ReadonlyArray<Column<GatewayEntry>> {
  return [
    {
      key: 'id',
      header: t('gateways.id'),
      render: (g) => <span className={screen.mono}>{g.gateway_id}</span>,
    },
    {
      key: 'core_name',
      header: t('gateways.coreName'),
      // Core's own connector name, from the warehouse rows. It is the context
      // that makes a bare numeric id labellable at all — real ids carry no
      // meaning of their own. Internal responses only; this screen is
      // internal-only anyway.
      render: (g) => g.core_name || '—',
    },
    {
      key: 'label',
      header: t('gateways.label'),
      render: (g) => {
        const draft = actions.draftLabel(g)
        const dirty = draft !== g.gateway_label
        return (
          <div className={styles.labelCell}>
            <input
              className={styles.labelInput}
              value={draft}
              placeholder={t('gateways.labelPlaceholder')}
              aria-label={t('gateways.label')}
              maxLength={255}
              onChange={(e) => actions.onLabelChange(g.gateway_id, e.target.value)}
              // Enter saves. The row is a form in spirit, and someone typing a
              // name reaches for Enter before they reach for a button.
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  actions.onLabelSave(g)
                }
              }}
            />
            {/* Only offered when there is something to save, so the column is
                not a wall of identical buttons. */}
            {dirty && (
              <Button
                size="sm"
                onClick={() => actions.onLabelSave(g)}
                loading={actions.busyId === g.gateway_id}
              >
                {t('gateways.save')}
              </Button>
            )}
          </div>
        )
      },
    },
    {
      key: 'display',
      header: t('gateways.display'),
      // Shown verbatim from the API so this column is proof of the agreed
      // format rather than a second implementation of it.
      render: (g) => g.gateway_name,
    },
    {
      key: 'biller',
      header: t('gateways.assignedTo'),
      render: (g) => (
        <div className={styles.assignCell}>
          <SelectField
            label={t('gateways.assignedTo')}
            hideLabel
            value={g.biller_id}
            disabled={actions.busyId === g.gateway_id}
            onChange={(e) => actions.onAssign(g, e.target.value)}
          >
            <option value="">{t('gateways.unassigned')}</option>
            {/* Providers only — kinds do not cross; see `providers` in the screen. */}
            {providers.map((b) => (
              <option key={b.biller_id} value={b.biller_id}>
                {b.name}
              </option>
            ))}
          </SelectField>
        </div>
      ),
    },
    {
      key: 'beneficiaries',
      header: t('gateways.beneficiaries'),
      // Identities come from payments only, so a gateway that recorded none
      // has nothing to assign: no action, rather than an empty panel.
      render: (g) =>
        (g.beneficiary_count ?? 0) > 0 ? (
          <Button size="sm" variant="secondary" onClick={() => actions.onOpenBeneficiaries(g)}>
            {t('gateways.beneficiaries.action', { count: g.beneficiary_count ?? 0, unassigned: g.unassigned_beneficiary_count ?? 0 })}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      key: 'state',
      header: t('billers.status'),
      render: (g) =>
        g.unmapped ? (
          <StatusBadge tone="warning">{t('gateways.unmapped')}</StatusBadge>
        ) : (
          <StatusBadge tone="success">{t('billers.active')}</StatusBadge>
        ),
    },
  ]
}

interface GatewaysProps {
  /** Seeds the biller filter — the deep link from the Billers screen
   *  (`/gateways?biller=CODE`). Seed-only, not synced back to the URL:
   *  arriving from another screen always mounts this one fresh. */
  readonly initialBiller?: string
}

export function Gateways({ initialBiller = '' }: GatewaysProps = {}) {
  const t = useT()
  const { notify } = useToast()

  const [query, setQuery] = useState('')
  const [unmappedOnly, setUnmappedOnly] = useState(false)
  const [biller, setBiller] = useState(initialBiller)

  // Filtering happens server-side so the three filters compose the same way
  // they do in the API's own tests, and so a large catalogue is never fully
  // shipped to the browser just to be hidden by it.
  const filter = useMemo(
    () => ({ ...(query ? { q: query } : {}), ...(unmappedOnly ? { unmapped: true } : {}), ...(biller ? { biller } : {}) }),
    [query, unmappedOnly, biller],
  )
  const gateways = useGateways(filter)
  const unfiltered = useGateways()
  const billers = useBillers(true)
  // Kinds do not cross: a beneficiary holds slices, never a gateway. The
  // assignment select and the biller filter offer providers, the
  // Beneficiaries panel offers beneficiaries — each list derived once here,
  // not inside a per-row render.
  const providers = useMemo(() => (billers.data ?? []).filter((b) => b.kind !== 'beneficiary'), [billers.data])
  const beneficiaryOrganisations = useMemo(() => (billers.data ?? []).filter((b) => b.kind === 'beneficiary'), [billers.data])
  const setLabel = useSetGatewayLabel()
  const assign = useAssignGateway()
  const assignSlice = useAssignSlice()
  const revokeSlice = useRevokeSlice()
  const [panelGateway, setPanelGateway] = useState<GatewayEntry | null>(null)
  const [busySlice, setBusySlice] = useState<string | null>(null)

  // Label drafts, keyed by gateway. Absent ⇒ show the saved value; present ⇒
  // the user is mid-edit and their text wins, even across a background
  // refetch that would otherwise overwrite what they are typing.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [busyId, setBusyId] = useState<string | null>(null)

  const fail = (error: unknown) =>
    notify('danger', error instanceof BffError || error instanceof Error ? error.message : t('error.title'))

  const rows = gateways.data ?? []
  const filtering = query !== '' || unmappedOnly || biller !== ''

  const columns = buildColumns(t, providers, {
    draftLabel: (g) => drafts[g.gateway_id] ?? g.gateway_label,
    onLabelChange: (id, value) => setDrafts((d) => ({ ...d, [id]: value })),
    onLabelSave: (g) => {
      const label = (drafts[g.gateway_id] ?? g.gateway_label).trim()
      setBusyId(g.gateway_id)
      setLabel.mutate(
        { id: g.gateway_id, label },
        {
          onSuccess: (saved) => {
            notify('success', t('gateways.saved', { name: saved.gateway_name }))
            // Drop the draft so the row goes back to reflecting the server.
            setDrafts((d) => {
              const { [g.gateway_id]: _dropped, ...rest } = d
              return rest
            })
          },
          onError: fail,
          onSettled: () => setBusyId(null),
        },
      )
    },
    onAssign: (g, billerId) => {
      setBusyId(g.gateway_id)
      assign.mutate(
        { id: g.gateway_id, billerId },
        {
          onSuccess: () => {
            const name = billers.data?.find((b) => b.biller_id === billerId)?.name ?? billerId
            notify(
              'success',
              billerId
                ? t('gateways.assigned', { gateway: g.gateway_name, biller: name })
                : t('gateways.detached', { gateway: g.gateway_name }),
            )
          },
          onError: fail,
          onSettled: () => setBusyId(null),
        },
      )
    },
    onOpenBeneficiaries: (g) => setPanelGateway(g),
    busyId,
  })

  const onSlice = (line: BeneficiaryLine, code: string) => {
    if (!panelGateway) return
    const gatewayId = panelGateway.gateway_id
    setBusySlice(line.beneficiary_code)
    const done = () => setBusySlice(null)
    if (code === '') {
      revokeSlice.mutate(
        { code: line.biller_id, gatewayId, beneficiaryCode: line.beneficiary_code },
        { onSuccess: () => notify('success', t('gateways.beneficiaries.revoked', { identity: line.beneficiary_code })), onError: fail, onSettled: done },
      )
      return
    }
    assignSlice.mutate(
      { code, gatewayId, beneficiaryCode: line.beneficiary_code },
      {
        onSuccess: () => {
          const name = billers.data?.find((b) => b.biller_id === code)?.name ?? code
          notify('success', t('gateways.beneficiaries.assigned', { identity: line.beneficiary_code, organisation: name }))
        },
        onError: fail,
        onSettled: done,
      },
    )
  }

  return (
    <section>
      <div className={screen.header}>
        <div>
          <h1 className={screen.title}>{t('gateways.title')}</h1>
          <p className={screen.subtitle}>{t('gateways.subtitle')}</p>
        </div>
      </div>

      <GatewayNote />

      <div className={styles.filters}>
        <TextField
          label={t('gateways.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <SelectField label={t('gateways.biller')} value={biller} onChange={(e) => setBiller(e.target.value)}>
          <option value="">{t('gateways.allBillers')}</option>
          {providers.map((b) => (
            <option key={b.biller_id} value={b.biller_id}>
              {b.name}
            </option>
          ))}
        </SelectField>
        <label className={styles.filterToggle}>
          <input type="checkbox" checked={unmappedOnly} onChange={(e) => setUnmappedOnly(e.target.checked)} />
          {t('gateways.unmappedOnly')}
        </label>
      </div>

      <div className={panelGateway ? styles.panelLayout : undefined}>
      <div>
      {gateways.isPending ? (
        <Skeleton variant="row" count={8} label={t('common.loading')} />
      ) : gateways.error ? (
        <EmptyState
          glyph="⚠"
          title={t('error.loadFailed')}
          body={gateways.error.message}
          actions={<Button onClick={() => void gateways.refetch()}>{t('error.retry')}</Button>}
        />
      ) : rows.length === 0 ? (
        // Two different empty states, because "your filters match nothing" and
        // "there is nothing here at all" call for different next actions.
        filtering ? (
          <EmptyState
            glyph="⌕"
            title={t('gateways.empty')}
            body={t('gateways.emptyBody')}
            actions={
              <Button
                variant="secondary"
                onClick={() => {
                  setQuery('')
                  setUnmappedOnly(false)
                  setBiller('')
                }}
              >
                {t('tx.reset')}
              </Button>
            }
          />
        ) : (
          <EmptyState glyph="⇄" title={t('gateways.emptyAll')} body={t('gateways.emptyAllBody')} />
        )
      ) : (
        <>
          <p className={styles.count}>
            {t('gateways.count', { shown: String(rows.length), total: String(unfiltered.data?.length ?? rows.length) })}
          </p>
          <DataTable
            caption={t('gateways.title')}
            columns={columns}
            rows={rows}
            rowKey={(g) => g.gateway_id}
            stale={gateways.isFetching}
            sortLabel={(column) => column}
          />
        </>
      )}
      </div>
      {panelGateway && (
        <BeneficiariesPanel
          key={panelGateway.gateway_id}
          gateway={panelGateway}
          organisations={beneficiaryOrganisations}
          busy={busySlice}
          onAssign={onSlice}
          onClose={() => setPanelGateway(null)}
        />
      )}
      </div>
    </section>
  )
}
